export interface SignedInUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'staff';
}

export type PeopleState =
  | { kind: 'signed-out' }
  | { kind: 'forbidden' }
  | { kind: 'error' }
  | { kind: 'ready'; users: SignedInUser[] };

const ERRORS: Record<string, string> = {
  last_admin: 'The last Admin cannot be removed or demoted.',
  exists: 'That email is already on the list.',
  invalid_email: 'Enter a valid email address.',
  invalid_role: 'That role is not valid.',
  invalid_json: 'That request was not valid.',
  forbidden: 'Only an Admin can manage people.',
  origin: 'That request was rejected.',
  unauthorized: 'Sign in again.',
  not_found: 'That person is no longer on the list.',
  unavailable: 'Mat Monitor could not reach its database.',
};

export async function readMe(): Promise<SignedInUser | null> {
  const response = await fetch('/api/auth/me');
  if (response.status === 401) return null;
  if (!response.ok) throw new Error('Sign-in status is unavailable.');
  return parseUser(await response.json());
}

export async function loadPeople(): Promise<PeopleState> {
  try {
    const response = await fetch('/api/users');
    if (response.status === 401) return { kind: 'signed-out' };
    if (response.status === 403) return { kind: 'forbidden' };
    if (!response.ok) return { kind: 'error' };
    const body: unknown = await response.json();
    if (typeof body !== 'object' || body === null || !('users' in body) || !Array.isArray(body.users)) {
      return { kind: 'error' };
    }
    return { kind: 'ready', users: body.users.map((entry) => parseUser(entry)) };
  } catch {
    return { kind: 'error' };
  }
}

export async function signOut(): Promise<boolean> {
  const response = await fetch('/api/auth/logout', { method: 'POST' });
  return response.ok;
}

export async function addStaffEmail(email: string): Promise<string | null> {
  const response = await fetch('/api/users', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (response.ok) return null;
  return readError(response);
}

export async function changePersonRole(id: string, role: 'admin' | 'staff'): Promise<string | null> {
  const response = await fetch(`/api/users/${id}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ role }),
  });
  if (response.ok) return null;
  return readError(response);
}

export async function removePerson(id: string): Promise<string | null> {
  const response = await fetch(`/api/users/${id}`, { method: 'DELETE' });
  if (response.ok) return null;
  return readError(response);
}

async function readError(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (typeof body === 'object' && body !== null && 'error' in body && typeof body.error === 'string') {
      return ERRORS[body.error] ?? 'That did not work.';
    }
  } catch {
    // The status is enough when the body is not JSON.
  }
  if (response.status === 403) return ERRORS.forbidden ?? 'Not allowed.';
  return 'That did not work.';
}

function parseUser(value: unknown): SignedInUser {
  if (typeof value !== 'object' || value === null) throw new Error('bad user');
  const record = value as Record<string, unknown>;
  if (record.role !== 'admin' && record.role !== 'staff') throw new Error('bad role');
  if (typeof record.id !== 'string' || typeof record.email !== 'string' || typeof record.name !== 'string') {
    throw new Error('bad user');
  }
  return { id: record.id, email: record.email, name: record.name, role: record.role };
}
